import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import startCase from 'lodash/startCase';
import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import { GlobalNav, invalidateMenuCache } from '@nexus/fff-global-nav';
import { GlobalFooter } from '@nexus/fff-global-footer';

import { CAMPAIGN_TYPE } from 'utils/campaign';
import { PLAN_CODE } from 'utils/user';
import {
  INVITE_STATE,
  REDIRECT_ACTION,
  sortPreviewProducts,
} from 'utils/social-ecomm';
import {
  getProductsByCampaignType,
  getProductByCampaignSku,
} from 'services/api/products';
import {
  getInviteData,
  getTeamId,
  reedemInviteAndJoinTeam,
} from 'services/api/social-ecomm';
import { getCampaign } from 'services/api/campaign';
import withViewSize from 'HOC/withViewSize/withViewSize';
import ShareSale from 'shared-components/ShareSale/ShareSale';
import PreviewProducts from 'shared-components/PreviewProducts/PreviewProducts';
import { Toast, showErrorToast } from 'shared-components/BaseToast/Toast';
import { selectPageTrackingData } from 'selectors/trackingSelector';
import { track } from 'utils/segment';
import useFacebookScript from 'hooks/useFacebookScript';

import Layout from './Layout/Layout';
import Separator from './Separator/Separator';
import HowRedeemWorks from './HowRedeemWorks/HowRedeemWorks';
import Banner from './Banner/Banner';

const getCtaState = (
  inviteState,
  inviteCode,
  handleRedeemInviteTracking,
  handleMemberRedeemInvite
) => {
  const CTA_STATE = {
    redeemed: {
      buttonText: 'login',
      buttonAction: REDIRECT_ACTION.login,
    },
    unredeemed: {
      buttonText: 'redeem invite',
      buttonAction: () => {
        handleRedeemInviteTracking('REDEEM INVITE');
        invalidateMenuCache();
        REDIRECT_ACTION.createAccount(inviteCode);
      },
    },
    expired: {
      buttonText: 'join now',
      buttonAction: REDIRECT_ACTION.getTheBox,
    },
    invalid: {
      buttonText: 'login',
      buttonAction: REDIRECT_ACTION.login,
    },
    member: {
      buttonText: 'redeem invite',
      buttonAction: () => {
        handleRedeemInviteTracking('REDEEM INVITE');
        handleMemberRedeemInvite();
      },
    },
  };
  return get(CTA_STATE, inviteState, {
    buttonText: '',
    buttonAction: () => {},
  });
};

const RedeemInvitePage = ({ viewSize, planCode, trackingData }) => {
  const [inviteData, setInviteData] = useState({});
  const [product, setProduct] = useState(null);
  const [previewProducts, setPreviewProducts] = useState([]);
  const [inviteState, setInviteState] = useState('');
  const [loadingInviteData, setLoadingInviteData] = useState(true);
  const [loadingProductData, setLoadingProductData] = useState(false);
  const history = useHistory();
  const { senderName, expiredTs } = inviteData;
  const urlParams = new URLSearchParams(window.location.search);
  const inviteCode = urlParams.get('inviteCode');
  const sku = urlParams.get('sku');
  const {
    socialEcomm: socialEcommFF,
    redemptionPageRedirect: redemptionPageRedirectFF,
  } = useFlags();
  useFacebookScript();

  const firstNameOrEmpty = senderName
    ? startCase(toLower(senderName.split(' ')[0]))
    : '';
  const firstNameOrHer = senderName
    ? startCase(toLower(senderName.split(' ')[0]))
    : 'her';

  const SHARE_SALE_COPY = {
    title: 'Experience Our Members-Only Sale for Free',
    subtitle: `Your friend ${firstNameOrEmpty} loves FabFitFun so much, they wanted you to get in on the fun. Create a login today to get a no-pressure, risk-free taste of FabFitFun.`,
    boxTitle: "You'll be able to",
    list: [
      `Shop and discover products with ${firstNameOrHer}.`,
      'Save up to 70% off on premium products.',
      'Invite others to join the fun.',
      'Earn a <strong>$10 credit</strong> to save even more on premium products.',
    ],
  };

  const handleRedeemInviteTracking = (ctaName) => {
    const { inviteCode, teamId, campaignId, campaignName } = inviteData;
    const data = {
      ...trackingData,
      campaign_id: campaignId,
      campaign_name: campaignName,
      invite_code: inviteCode,
      team_id: teamId,
      cta_name: ctaName,
      ...(product && {
        product_id: product.productId,
        product_name: product.productName,
      }),
    };
    track('Invite Redemption Started', data);
  };

  const handleMemberRedeemInvite = async () => {
    try {
      const response = await reedemInviteAndJoinTeam(inviteCode);
      if (response && response.ok) {
        REDIRECT_ACTION.edit();
      } else {
        throw new Error(response.message || 'Error');
      }
    } catch (error) {
      showErrorToast(
        'Redemption Unsuccessful',
        'We had an issue redeeming your invite. Please try again.'
      );
    }
  };

  const { buttonAction, buttonText } = getCtaState(
    inviteState,
    inviteCode,
    handleRedeemInviteTracking,
    handleMemberRedeemInvite
  );

  useEffect(() => {
    const auth = !!planCode;
    const fetchProductsByCampaignType = async () => {
      const response = await getProductsByCampaignType(
        CAMPAIGN_TYPE.edit,
        auth
      );
      const data = get(response, 'data.items[0]', {});
      const { productIds, products } = data;
      const previewProductsData = sortPreviewProducts(productIds, products);
      setPreviewProducts(previewProductsData);
    };
    const fetchProductByCampaignSku = async (campaignId, sku) => {
      try {
        setLoadingProductData(true);
        const response = await getProductByCampaignSku(campaignId, sku);
        const data = get(response, 'data.items[0]', {});
        setProduct(data);
        setLoadingProductData(false);
      } catch (error) {
        console.error(error);
      }
    };
    const handleMemberRedeem = async () => {
      const campaignData = await getCampaign(CAMPAIGN_TYPE.edit);
      const { id, campaignWindow } = campaignData;
      const teamData = await getTeamId(id, campaignWindow);
      const teamId = get(teamData, 'data.items[0].teamId');
      if (teamId) {
        history.push('/edit');
      } else {
        fetchProductsByCampaignType();
      }
    };
    const handleInviteState = async () => {
      try {
        const response = await getInviteData(inviteCode);
        const data = get(response, 'data.items[0]', {});
        const isRedeemableMember = auth && planCode !== PLAN_CODE.fffsocial;
        if (isRedeemableMember) {
          setInviteState(INVITE_STATE.member);
          handleMemberRedeem();
        } else if (auth) {
          history.push('/edit');
        } else if (moment(data.expiredTs).isBefore(new Date())) {
          // expired invite
          fetchProductsByCampaignType();
          setInviteState(INVITE_STATE.invite_expired);
        } else {
          // unredeemed invite
          fetchProductsByCampaignType();
          setInviteState(INVITE_STATE.unredeemed);
          if (sku) fetchProductByCampaignSku(data.campaignId, sku);
        }
        setInviteData(data);
        setLoadingInviteData(false);
      } catch (err) {
        if (auth) {
          history.push('/edit');
        } else {
          setInviteState(INVITE_STATE.invite_invalid);
          fetchProductsByCampaignType();
          setLoadingInviteData(false);
        }
      }
    };

    if (socialEcommFF) {
      if (redemptionPageRedirectFF) {
        REDIRECT_ACTION.getTheBox();
      }
      if (inviteCode) {
        handleInviteState();
      } else if (auth) {
        REDIRECT_ACTION.edit();
      } else {
        REDIRECT_ACTION.login();
      }
    } else if (auth && planCode === PLAN_CODE.fffsocial) {
      REDIRECT_ACTION.subscribe();
    } else if (auth) {
      REDIRECT_ACTION.edit();
    } else {
      REDIRECT_ACTION.getTheBox();
    }
  }, [planCode]);

  if (loadingInviteData || loadingProductData) return null;

  return (
    <>
      <GlobalNav />
      <Banner
        senderName={senderName}
        expirationDate={expiredTs}
        inviteState={inviteState}
        buttonText={buttonText}
        buttonAction={buttonAction}
        product={product}
        viewSize={viewSize}
      />
      <Layout>
        <HowRedeemWorks />
        <Separator />
        <PreviewProducts
          products={previewProducts}
          viewSize={viewSize}
          inviteState={inviteState}
          inviteCode={inviteCode}
          handleRedeemInviteTracking={handleRedeemInviteTracking}
        />
        <Separator />
        <ShareSale
          title={SHARE_SALE_COPY.title}
          subtitle={SHARE_SALE_COPY.subtitle}
          boxTitle={SHARE_SALE_COPY.boxTitle}
          list={SHARE_SALE_COPY.list}
          buttonText={buttonText}
          viewSize={viewSize}
          onClickButton={buttonAction}
        />
      </Layout>
      <Toast />
      <GlobalFooter hideChatWidget />
    </>
  );
};

RedeemInvitePage.propTypes = {
  viewSize: PropTypes.string.isRequired,
  planCode: PropTypes.string,
  trackingData: PropTypes.PropTypes.shape({}).isRequired,
};

RedeemInvitePage.defaultProps = {
  planCode: '',
};

const mapStateToProps = (state) => ({
  viewSize: state.viewState.viewSize,
  planCode: state.userState.planCode,
  trackingData: selectPageTrackingData(state),
});

export default compose(
  withViewSize,
  connect(mapStateToProps, {})
)(RedeemInvitePage);
